// useVariantObservations — observation tier for the variant picker,
// parallel to useBrandObservations.
//
// Surfaces user-picked variants from `manual_corrections` keyed on
// (canonical_id, brand). Each row is `(canonical_id, brand, variant,
// correction_count, last_seen_at)`. For the variant picker we only
// need (variant, count).
//
// Why: today the variant picker pulls solely from the bundled
// BRAND_EXPERTISE catalog + ingredient_info.brands.common. When a
// brand is genuinely missing a variant entry (or the curated list
// hasn't caught up), the user picks "Wild Cherry" once via the
// custom-entry path — and the next time they scan a different
// Pepsi UPC, the same variant has to be re-picked. This hook closes
// that loop: every commit teaches the (canonical, brand) → variant
// mapping, and future pickers surface observed variants as pills.
//
// Cache discipline (per CLAUDE.md egress rules) — mirrors
// useBrandObservations exactly:
//   • In-memory map keyed by `${canonicalId}|${brand}`, 5-min TTL.
//   • localStorage layer with 24h TTL — admin promotions don't need
//     realtime here.
//   • No realtime subscription.

import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { supabase } from "./supabase";

const MEMORY_TTL_MS  = 5 * 60 * 1000;
const LOCAL_TTL_MS   = 24 * 60 * 60 * 1000;
const LOCAL_KEY      = "mise:variant_observations:cache:v1";

const memoryCache = new Map(); // key → { fetchedAt, rows }

function keyFor(canonicalId, brand) {
  return `${canonicalId || ""}|${(brand || "").toLowerCase().trim()}`;
}

function readLocal() {
  try {
    if (typeof window === "undefined") return null;
    const raw = window.localStorage?.getItem(LOCAL_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch { return null; }
}

function writeLocal(map) {
  try {
    if (typeof window === "undefined") return;
    window.localStorage?.setItem(LOCAL_KEY, JSON.stringify(map));
  } catch { /* quota / private mode — harmless */ }
}

async function fetchObservations(canonicalId, brand) {
  if (!canonicalId || !brand) return [];
  const { data, error } = await supabase
    .from("manual_corrections")
    .select("variant, correction_count")
    .eq("canonical_id", canonicalId)
    .eq("brand", brand)
    .not("variant", "is", null);
  if (error) {
    // Migration 0160 not applied or RLS misconfigure. Non-fatal — the
    // picker falls back to the catalog tier (BRAND_EXPERTISE +
    // info.brands.common). Log once per session at most.
    if (!fetchObservations._warned) {
      console.warn("[useVariantObservations] fetch failed:", error.message);
      fetchObservations._warned = true;
    }
    return [];
  }
  // Aggregate by variant — manual_corrections has one row per
  // (canonical, brand, variant), so the variant value is already
  // unique here. Just map.
  return (data || [])
    .map((r) => ({
      name:  typeof r.variant === "string" ? r.variant.trim() : "",
      count: Number(r.correction_count) || 1,
    }))
    .filter((r) => !!r.name);
}

/**
 * useVariantObservations(canonicalId, brand)
 *
 * Returns: { observations, loading, clear }
 *   observations: array of { name, count } — empty until fetched
 *   loading:      true while the network request is in flight
 *   clear(key?):  invalidate cache so the next render refetches
 *
 * Pass null/undefined canonicalId or brand to no-op (returns []).
 */
export function useVariantObservations(canonicalId, brand) {
  const k = canonicalId && brand ? keyFor(canonicalId, brand) : null;
  const [rows, setRows] = useState(() => {
    if (!k) return [];
    const mem = memoryCache.get(k);
    if (mem && Date.now() - mem.fetchedAt < MEMORY_TTL_MS) return mem.rows;
    const local = readLocal();
    const localRow = local?.[k];
    if (localRow && Date.now() - localRow.fetchedAt < LOCAL_TTL_MS) {
      memoryCache.set(k, localRow);
      return localRow.rows;
    }
    return [];
  });
  const [loading, setLoading] = useState(false);
  const aliveRef = useRef(true);

  useEffect(() => () => { aliveRef.current = false; }, []);

  useEffect(() => {
    if (!k) {
      setRows([]);
      return;
    }
    const mem = memoryCache.get(k);
    if (mem && Date.now() - mem.fetchedAt < MEMORY_TTL_MS) {
      setRows(mem.rows);
      return;
    }
    setLoading(true);
    let cancelled = false;
    (async () => {
      const rs = await fetchObservations(canonicalId, brand);
      if (cancelled || !aliveRef.current) return;
      memoryCache.set(k, { fetchedAt: Date.now(), rows: rs });
      const local = readLocal() || {};
      local[k] = { fetchedAt: Date.now(), rows: rs };
      writeLocal(local);
      setRows(rs);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [k, canonicalId, brand]);

  const clear = useCallback((kk) => {
    if (kk) {
      memoryCache.delete(kk);
      const local = readLocal() || {};
      delete local[kk];
      writeLocal(local);
    } else {
      memoryCache.clear();
      writeLocal({});
    }
  }, []);

  return useMemo(() => ({ observations: rows, loading, clear }), [rows, loading, clear]);
}
